<script setup lang="ts">
/** The A4 print pack (#printRoot): band, watermark, header, hero, elevation, cut list, datums, profile. */
import { computed } from 'vue';
import { packModel } from '@/export';
import ElevationSvg from './ElevationSvg.vue';
import SemblaroLogo from './SemblaroLogo.vue';
import CutTable from './CutTable.vue';
import { snap, ui } from '@/app/state';
import { receiptFor } from '@/app/checks';
import { exportBlocker } from '@/app/navigation';
import { contractOf } from '@/families/contract';

const s = computed(() => snap.value);
const revisionId = computed(() => (ui.print ? ui.print.revisionId : s.value.currentRevisionId));
const dirty = computed(() => (ui.print ? ui.print.dirty : s.value.isDirty));
/**
 * Phase 3: the pack prints only for a saved, unedited revision whose current assembly passed the
 * independent check. The browser can print at any moment (⌘P does not wait for a check), so the
 * content itself is gated: otherwise the page says how to get a checked pack.
 */
const printable = computed(() => {
  void ui.checks;
  const rid = revisionId.value;
  if (!rid || dirty.value || exportBlocker()) return false;
  return receiptFor(rid, contractOf(s.value.build).assemblyHash)?.status === 'passed';
});
const pack = computed(() => packModel(s.value.build, { revisionId: revisionId.value, dirty: dirty.value, fields: s.value.fields, generatedAt: ui.print?.at ?? s.value.revisions.at(-1)?.createdAt ?? new Date(0).toISOString() }, { construction: s.value.construction }));
</script>

<template>
  <Teleport to="body">
    <div v-if="!printable" id="printRoot" class="print-root" aria-hidden="true" data-print="not-checked">
      <header class="pr-head"><div><span class="pr-logo"><SemblaroLogo /></span><p class="pr-kicker">Build pack</p><h1>Not ready to print</h1>
        <p>This design has not passed the independent check of its joints, supports and stock fit{{ revisionId && !dirty ? ` (${revisionId})` : '' }}. Open Pack, choose Check now or Export the pack, then print from there.</p></div></header>
    </div>
    <div v-else id="printRoot" class="print-root" aria-hidden="true">
      <div class="pr-band" :class="{ 'is-ok': !pack.watermark && s.build.validation.ok }">{{ pack.band }}</div>
      <div v-if="pack.watermark" class="pr-water" aria-hidden="true">{{ pack.watermark.toUpperCase() }}</div>
      <header class="pr-head">
        <div>
          <span class="pr-logo"><SemblaroLogo /></span><p class="pr-kicker">Build pack</p>
          <h1>{{ pack.title }}</h1>
          <p>{{ pack.familyLabel }} · {{ pack.overallText }} · {{ pack.shelves }} shelves{{ s.build.bays.length > 1 ? ` in ${s.build.bays.length} bays` : '' }}</p>
        </div>
        <div class="pr-meta">
          <p><strong>{{ pack.revisionText }}</strong> · hash <span class="mono">{{ pack.hashShort }}</span></p>
          <p>{{ pack.header.stockLine }}</p>
          <p>{{ pack.header.profileLine }}</p>
          <p>{{ pack.header.printedLine }}</p>
        </div>
      </header>
      <section class="pr-figs">
        <figure v-if="ui.print?.hero"><img :src="ui.print.hero" alt=""><figcaption>Preview, three-quarter view</figcaption></figure>
        <figure class="pr-elev"><ElevationSvg :build="s.build" :interactive="false" /><figcaption>Front elevation · openings (right) and shelf undersides from the floor (left), mm</figcaption></figure>
      </section>
      <section>
        <h2>Cut list</h2>
        <template v-for="g in pack.cutGroups" :key="g.stockId">
          <h3 class="pr-group">{{ g.title }}</h3>
          <CutTable :build="s.build" :link="false" :part-ids="g.rows.map((r) => r.id)" :caption="`Cut list: ${g.title}`" />
        </template>
        <p class="pr-note">Finished sizes in mm. {{ pack.cutCaption.replace(' Select a row to find the part on the model.', '') }}</p>
        <template v-if="pack.glueUps.length"><h3 class="pr-group">Glue-ups</h3><ul><li v-for="g in pack.glueUps" :key="g">{{ g }}</li></ul></template>
        <p v-if="pack.massText" class="pr-note">{{ pack.massText }}</p>
      </section>
      <section class="pr-two">
        <div>
          <h2>Shelf datums</h2>
          <ol><li v-for="dt in pack.datums" :key="dt.partId">{{ dt.label }}: {{ dt.text }}</li></ol>
          <h2>Sheet fit</h2>
          <p>{{ pack.sheetFit.text }}</p>
        </div>
        <div>
          <h2>Construction profile <small>({{ pack.profile.tag }})</small></h2>
          <dl class="kv kv--long"><div v-for="r in pack.profile.rows" :key="r.label"><dt>{{ r.label }}</dt><dd>{{ r.text }}</dd></div></dl>
        </div>
      </section>
      <section v-if="pack.fronts">
        <h2>Doors and drawers <small>({{ pack.fronts.summary }})</small></h2>
        <table class="pr-table"><thead><tr><th>Hardware to buy</th><th>Qty</th><th>Specification</th></tr></thead>
          <tbody><tr v-for="h in pack.fronts.hardware" :key="h.kind"><td>{{ h.kind }}</td><td>{{ h.qty }}</td><td>{{ h.spec }}</td></tr></tbody></table>
        <template v-if="pack.fronts.glass.length"><h3>Glass to order</h3><ul><li v-for="g in pack.fronts.glass" :key="g">{{ g }}</li></ul></template>
        <h3>Fitting</h3><ol><li v-for="(f, i) in pack.fronts.fitting" :key="i">{{ f }}</li></ol>
      </section>
      <section><h2>Assembly</h2><ol><li v-for="(st, i) in pack.profile.assemblySteps" :key="i">{{ st }}</li></ol></section>
      <footer class="pr-foot">{{ pack.footer }}</footer>
    </div>
  </Teleport>
</template>
